import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Modal } from '../components/Modal';
import styles from './Knowledges.module.scss';

export const KnowledgesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') || '';

  const [knowledges, setKnowledges] = useState([]);
  const [aiLearnt, setAiLearnt] = useState(false);
  const [userUrl, setUserUrl] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');
  const [informLoading, setInformLoading] = useState(false);
  const [isInformConfirmOpen, setIsInformConfirmOpen] = useState(false);

  useEffect(() => {
    if (!userId) {
      setKnowledges([]);
      setUserUrl('');
      return;
    }

    const fetchKnowledges = async () => {
      try {
        setIsLoading(true);
        setError('');
        const { data } = await api.get(`/admin/knowledges/${userId}`);
        setKnowledges(Array.isArray(data?.knowledges) ? data.knowledges : []);
        setAiLearnt(data?.aiLearnt === true);
        setUserUrl(data?.url || data?.userUrl || data?.siteUrl || '');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load knowledges');
        setKnowledges([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchKnowledges();
  }, [userId]);

  const filteredKnowledges = useMemo(() => {
    if (!search) return knowledges;
    const q = search.toLowerCase();
    return knowledges.filter(item => {
      const title = (item.title || '').toLowerCase();
      const text = (item.text || '').toLowerCase();
      return title.includes(q) || text.includes(q);
    });
  }, [knowledges, search]);

  const handleAddClick = () => {
    if (!userId) {
      alert('No user specified.');
      return;
    }
    setNewTitle('');
    setNewText('');
    setIsAddOpen(true);
  };

  const handleAddClose = () => {
    setIsAddOpen(false);
    setNewTitle('');
    setNewText('');
  };

  const handleAddConfirm = async () => {
    if (!userId) return;
    if (!newTitle.trim() && !newText.trim()) {
      alert('Please fill at least title or text.');
      return;
    }

    try {
      await api.post('/admin/knowledges', {
        userId,
        title: newTitle.trim(),
        text: newText.trim(),
      });

      const { data } = await api.get(`/admin/knowledges/${userId}`);
      setKnowledges(Array.isArray(data?.knowledges) ? data.knowledges : []);
      if (data?.aiLearnt !== undefined) setAiLearnt(data.aiLearnt === true);
      if (
        data?.url !== undefined ||
        data?.userUrl !== undefined ||
        data?.siteUrl !== undefined
      )
        setUserUrl(data?.url || data?.userUrl || data?.siteUrl || '');
      handleAddClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create knowledge');
    }
  };

  const handleInformConfirmOpen = () => setIsInformConfirmOpen(true);
  const handleInformConfirmClose = () => setIsInformConfirmOpen(false);

  const handleInformAiLearnt = async () => {
    if (!userId) return;
    try {
      setInformLoading(true);
      await api.patch(`/admin/users/${userId}/aiLearnt`);
      setAiLearnt(true);
      handleInformConfirmClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to inform AI learnt');
    } finally {
      setInformLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <button
          type="button"
          className={styles.backLink}
          onClick={() => navigate('/admin/dashboard')}
        >
          ← Back to dashboard
        </button>
        {userId && (
          <div style={{ marginBottom: '0.75rem' }}>
            <button
              type="button"
              onClick={handleAddClick}
              style={{
                padding: '0.45rem 0.9rem',
                borderRadius: '9999px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Add knowledge
            </button>
          </div>
        )}
        <h1 className={styles.header}>Knowledge base</h1>
        {userUrl && (
          <p className={styles.userUrl}>
            URL:{' '}
            <a href={userUrl} target="_blank" rel="noopener noreferrer">
              {userUrl}
            </a>
          </p>
        )}

        {userId && (
          <div style={{ marginBottom: '1rem', maxWidth: '320px' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by title or text..."
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #d1d5db',
                fontSize: '0.9rem',
              }}
            />
          </div>
        )}

        {!userId && (
          <p className={styles.error}>
            No user specified. Please return and select a user.
          </p>
        )}

        {userId && isLoading && (
          <p className={styles.empty}>Loading knowledges...</p>
        )}

        {userId && !isLoading && error && (
          <p className={styles.error}>{error}</p>
        )}

        {userId && !isLoading && !error && filteredKnowledges.length === 0 && (
          <p className={styles.empty}>No data.</p>
        )}

        {userId && filteredKnowledges.length > 0 && (
          <div className={styles.list}>
            {filteredKnowledges.map(item => (
              <article key={item._id} className={styles.item}>
                <h2 className={styles.title}>{item.title || 'Untitled'}</h2>
                <p className={styles.text}>{item.text || ''}</p>
              </article>
            ))}
          </div>
        )}

        {userId && aiLearnt === false && (
          <div style={{ marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={handleInformConfirmOpen}
              disabled={informLoading}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: 'none',
                backgroundColor: informLoading ? '#9ca3af' : '#2563eb',
                color: '#ffffff',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: informLoading ? 'default' : 'pointer',
              }}
            >
              {informLoading ? 'Sending...' : 'Inform AI is learnt'}
            </button>
          </div>
        )}

        <Modal
          isOpen={isInformConfirmOpen}
          title="Confirm"
          onClose={handleInformConfirmClose}
          onConfirm={handleInformAiLearnt}
          confirmLabel="Yes, inform"
        >
          <p style={{ margin: 0 }}>
            Are you sure you want to mark AI as learnt for this user?
          </p>
        </Modal>

        <Modal
          isOpen={isAddOpen}
          title="Add knowledge"
          onClose={handleAddClose}
          onConfirm={handleAddConfirm}
          confirmLabel="Create"
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  marginBottom: '0.25rem',
                }}
              >
                Title
              </label>
              <input
                type="text"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  marginBottom: '0.25rem',
                }}
              >
                Text
              </label>
              <textarea
                value={newText}
                onChange={e => setNewText(e.target.value)}
                rows={4}
                maxLength={1000}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                  resize: 'vertical',
                }}
              />
              <div
                style={{
                  marginTop: '0.25rem',
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  textAlign: 'right',
                }}
              >
                {newText.length}/1000
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};
